import React, { useState, useEffect, useRef, memo } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate, Outlet, useOutletContext } from 'react-router-dom';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import NexusOverlay from './components/NexusOverlay';
import LoadingScreen from './components/LoadingScreen';

import { 
  Package2, 
  Users, 
  FileText, 
  FileBarChart2, 
  Building2, 
  ChevronDown, 
  Plus, 
  Menu, 
  Search,
  X,
  Phone,
  Grid3X3,
  MoreVertical,
  DollarSign,
  Briefcase,
  Activity,
  BarChart3,
  Wrench,
  Globe,
  CreditCard,
  LogOut,
  Bot,
  User,
  CheckSquare,
  Settings2,
  Clock,
  Megaphone,
  GlobeIcon,
  MapPin,
  BarChart,
  TrendingUp,
  LineChart,
  MessageSquare,
  ListChecks
} from 'lucide-react';
import Services from './pages/Services';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Stock from './pages/Stock';
import SellProducts from './pages/SellProducts';
import OnlineStore from './pages/OnlineStore';
import AppStore from './pages/AppStore';
import ErrorBoundary from './components/ErrorBoundary';
import Proposals from './pages/Proposals';
import AIInsights from './pages/AIInsights';
import Resources from './pages/Resources';
import MobileSettings from './pages/settings/MobileSettings';
import Payments from './pages/Payments';
import Workflows from './pages/Workflows';
import Bookings from './pages/Bookings';
import ManagePayroll from './pages/ManagePayroll';
import TimeAccounting from './pages/TimeAccounting';
import Settings from './pages/Settings';
import Marketing from './pages/Marketing';
import FlatRates from './pages/settings/FlatRates';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import Setup from './pages/auth/Setup';
import ResetPassword from './pages/auth/ResetPassword';
import UpdatePassword from './pages/auth/UpdatePassword';
import AdminSubscriptionManagement from './pages/AdminSubscriptionManagement';
import ResourceSubscriptionExpired from './components/ResourceSubscriptionExpired';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import TeamReports from './pages/TeamReports';
import GPS from './pages/GPS';
import PrivateRoute from './components/PrivateRoute';
import AuthGuard from './components/AuthGuard';
import { supabase } from './lib/supabaseClient';
import { BusinessProvider, useBusiness } from './contexts/BusinessContext';
// import { UserProvider } from './contexts/UserContext'; // Temporarily disabled
import { DataCacheProvider } from './contexts/DataCacheContext';
import Profile from './pages/Profile';
import { getResourceByAuthId } from './lib/resources';
import { setupInspectionPhotosBucket } from './lib/storage';
import { SupabaseProvider } from './contexts/SupabaseContext';
import { SimpleTimerProvider } from './contexts/SimpleTimerContext';
import { MouseEvent as DOMMouseEvent } from 'react';
import SalesReports from './pages/SalesReports';
import Sales from './pages/Sales';
import ProfitLoss from './pages/ProfitLoss';
import LeftNavigation from './components/LeftNavigation';
import PublicReceipt from './pages/PublicReceipt';
import PublicPDFReceipt from './pages/PublicPDFReceipt';
import PublicProposal from './pages/PublicProposal';
import FloatingActionButton from './components/FloatingActionButton';
import MobileBottomNavigation from './components/MobileBottomNavigation';
import Timeclock from './pages/Timeclock';
import Tasks from './pages/Tasks';
import { authService } from './services/authService';
import { navigationService } from './services/navigationService';

import { NotificationsProvider } from './contexts/NotificationsContext';
// UnreadTasksBadge import removed
// import CashFlow from './pages/CashFlow'; // Disabled
import BookingStatus from './contexts/BookingStatusContext';
import BookOnline from './pages/public/BookOnline';
import BookingSuccess from './pages/public/BookingSuccess';
import PublicLeadForm from './pages/public/PublicLeadForm';
import PayInvoice from './pages/public/PayInvoice';
import StoreOnline from './pages/public/StoreOnline';
import Messages from "./pages/Messages";
import Automations from "./pages/Automations"; // Keeping for fallback - disabled in routing
import AutomationsV2 from "./pages/AutomationsV2";
import OnlineBookings from './pages/marketing/OnlineBookings';
import OnlineBookingEditor from './pages/OnlineBookingEditor';
import { ToastProvider } from './components/toasts/SimpleToast';
import { TasksDropdown } from './components/TasksDropdown';
import BusinessProfileDropdown from './components/BusinessProfileDropdown';

import CreateBookingModal from './components/CreateBookingModal';
import CreateProposalModal from './components/CreateProposalModal';
import WithdrawStockModal from './components/WithdrawStockModal';
import PaymentModal from './components/PaymentModal';
import { migrateLocalStorageToDatabase } from './utils/migrations';
import { useReminderProcessor } from './hooks/useReminderProcessor';

import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { UnreadOpportunitiesProvider } from './contexts/UnreadOpportunitiesContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { TwilioMessagePollerProvider } from './contexts/GlobalMessagePollerContext';
import { CallProvider } from './contexts/CallContext';
import { ApplicationSettingsProvider, useApplicationSettingsContext } from './contexts/ApplicationSettingsContext';
import { useSelectInputOnFocus } from './lib/utils';
import { PageId } from './hooks/useApplicationSettings';
import { notifyNewMessage } from './lib/notificationSender';
import CalendarV2 from './pages/CalendarV2';
import MobileCalendar from './pages/MobileCalendar';

import SuppliesUsage from './pages/SuppliesUsage';
import CRM from './pages/CRM';
import CreateMobileBookingModal from './components/CreateMobileBookingModal';
import TestNotification from './pages/TestNotification';
import ToastContainer from './components/ToastContainer';
import Dialler from './pages/Dialler';
import CallsBeta from './pages/CallsBeta';
import FeedbackPage from './pages/FeedbackPage';
import LiabilityWaiver from './pages/public/LiabilityWaiver';
import BookingInfoModal from './components/BookingInfoModal';
import KPIDashboard from './pages/KPIDashboard';
import { PermissionProvider } from './contexts/PermissionContext';
import PermissionGuard from './components/PermissionGuard';
import { usePermissionCheck } from './hooks/usePermissionCheck';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { mobileService } from './lib/mobile';
import { authStateManager, AuthStateManager } from './lib/authStateManager';
import { sessionBackupManager } from './lib/sessionBackupManager';
import { sessionMonitor } from './lib/sessionMonitor';
// Removed all persistence imports
// Removed SessionStatusIndicator
import RexTesting from './pages/RexTesting';
import Website from './website/Website';
import RexTraining from './pages/RexTraining';


// Development flag to control feature visibility
const isDevelopment = process.env.NODE_ENV === "development";

interface AppContentProps {
  // Remove task-related props
}

interface MenuItem {
  to: string;
  icon: React.ReactNode;
  text: string;
  permission?: string;
  onClick?: () => void;
}

const menuCategories = [
  {
    name: "Manage",
    items: [
      {
        to: "/timeclock",
        icon: <Clock className="h-4 w-4" />,
        text: "Timeclock",
        permission: "timeclock",
      },
      {
        to: "/payroll",
        icon: <DollarSign className="h-4 w-4" />,
        text: "Payroll",
        permission: "payroll",
      },
      {
        to: "/stock",
        icon: <Package2 className="h-4 w-4" />,
        text: "Supplies",
        permission: "stock",
      },
      {
        to: "/resources",
        icon: <Users className="h-4 w-4" />,
        text: "Resources",
        permission: "resources",
      },
      {
        to: "/services",
        icon: <Wrench className="h-4 w-4" />,
        text: "Services",
        permission: "services",
      },
    ] as MenuItem[],
  },
  {
    name: "Reports",
    items: [
      {
        to: "/sales-reports",
        icon: <BarChart className="h-4 w-4" />,
        text: "Sales Reports",
        permission: "sales_reports",
      },
    ] as MenuItem[],
  },
];

interface TopNavLinkProps {
  to: string;
  children: React.ReactNode;
}

function TopNavLink({ to, children }: TopNavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const isAIInsights = to === "/insights";

  return (
    <Link
      to={to}
      className={`text-[13px] text-white/90 hover:text-white relative py-4 px-3 transition-all duration-300 ease-out rounded-lg hover:bg-white/10 ${
        isActive ? "text-white font-medium" : ""
      } ${isAIInsights ? "ai-insights-button px-3 py-1.5 rounded-lg" : ""}`}
    >
      {children}
      {isActive && !isAIInsights && (
        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-white rounded-full transition-all duration-300 ease-out" />
      )}
    </Link>
  );
}

const AppContent = memo(function AppContent() {
  console.log('🔍 AppContent function called - console logging test');
  
  const location = useLocation();
  

  // Initialize sidebar state based on viewport size
  const initialIsMobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = useState(!initialIsMobile);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [unviewedMissedCount, setUnviewedMissedCount] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const appContentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [hasBookings, setHasBookings] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);

  const [proposalsAccess, setProposalsAccess] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  const [hasPayments, setHasPayments] = useState(false);
  const [hasCustomers, setHasCustomers] = useState(false);
  const [hasPayroll, setHasPayroll] = useState(false);

  const [hasSettings, setHasSettings] = useState(false);



  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [bottomNavItems, setBottomNavItems] = useState<string[]>([]);

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { selectedBusiness, loading: businessLoading, error: businessError, forceLoadBusinesses } = useBusiness();
  const [userInfo, setUserInfo] = useState<{ avatarUrl?: string } | null>(null);
  
  // Nexus AI Assistant state
  const [isNexusOverlayOpen, setIsNexusOverlayOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [bookingsPermissionLevel, setBookingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");

  const [timeclockPermissionLevel, setTimeclockPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");

  const [settingsPermissionLevel, setSettingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  // Task-related hooks removed
  const [hasMessages, setHasMessages] = useState(false);
  // Get the application settings context to check for disabled pages
  const { isPageDisabled } = useApplicationSettingsContext();
  
  // Get permission checking functionality
  const { canAccess, isLoading: permissionsLoading } = usePermissionCheck();
  
  // FAB-related modal states - moved up for access by MobileBottomNavigation
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [isCreateWarrantyModalOpen, setIsCreateWarrantyModalOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  
  // Helper function to check if a page should be shown based on permissions and disabled status
  const shouldShowPage = (pageId: PageId, hasPermission: boolean) => {
    return hasPermission && !isPageDisabled(pageId);
  };

  // PWA detection effect
  useEffect(() => {
    // Method 1: Check for display-mode: standalone
    const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 2: Check for iOS PWA (using window.navigator)
    const isIOSPWA = 
      // @ts-ignore - 'standalone' exists on iOS Safari but not in the TypeScript definition
      (window.navigator as any).standalone || 
      window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 3: Check for PWA launch parameters
    const urlParams = new URLSearchParams(window.location.search);
    const launchedAsPWA = urlParams.get('pwa') === 'true';
    
    // Method 4: Check for iOS specific user agent
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    // Force PWA mode for testing if needed
    // const isPWALocal = true; // Uncomment for testing
    const isPWALocal = isDisplayModeStandalone || isIOSPWA || launchedAsPWA;
    
    // Update state
    setIsPWA(isPWALocal);
    
    // Add PWA class to body if in PWA mode
    if (isPWALocal) {
      document.body.classList.add('pwa-mode');
      
      // Set URL parameter for any page loads - ONLY if not already set and not already launched as PWA
      if (!launchedAsPWA && !urlParams.has('pwa')) {
        try {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('pwa', 'true');
          // Use replaceState instead of pushState to avoid adding to history stack
          window.history.replaceState({path: newUrl.toString()}, '', newUrl.toString());
        } catch (error) {
          console.warn('Failed to update URL for PWA mode:', error);
        }
      }
      
      // Apply iOS-specific classes if needed
      if (isIOS) {
        document.body.classList.add('ios-pwa-mode');
        // Fix for iOS bottom nav positioning
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (viewportMeta) {
          viewportMeta.setAttribute('content', 
            'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
        } else {
          // Create viewport meta if it doesn't exist
          const meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content = 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no';
          document.head.appendChild(meta);
        }
      }
      
      console.log('Running in PWA mode', { isIOS, isDisplayModeStandalone, isIOSPWA });
    } else {
      document.body.classList.remove('pwa-mode');
      document.body.classList.remove('ios-pwa-mode');
      console.log('Running in browser mode');
    }
  }, []); // Empty dependency array to run only once on mount

  // Add/remove page-specific classes for PWA fixes
  useEffect(() => {
    // Remove all page-specific classes first
    document.body.classList.remove('proposals-page-active', 'bookings-page-active', 'resources-page-active', 'services-page-active', 'crm-page-active', 'customers-page-active', 'kpi-dashboard-page-active', 'tasks-page-active', 'marketing-page-active', 'time-accounting-page-active', 'timeclock-page-active', 'automations-page-active');
    
    // Add appropriate class based on current page
    if (location.pathname === '/proposals') {
      document.body.classList.add('proposals-page-active');
    } else if (location.pathname === '/bookings') {
      document.body.classList.add('bookings-page-active');
    } else if (location.pathname === '/resources') {
      document.body.classList.add('resources-page-active');
    } else if (location.pathname === '/services') {
      document.body.classList.add('services-page-active');
    } else if (location.pathname === '/crm') {
      document.body.classList.add('crm-page-active');
    } else if (location.pathname === '/customers') {
      document.body.classList.add('customers-page-active');
    } else if (location.pathname === '/kpi-dashboard') {
      document.body.classList.add('kpi-dashboard-page-active');
    } else if (location.pathname === '/tasks') {
      document.body.classList.add('tasks-page-active');
    } else if (location.pathname === '/marketing' || location.pathname.startsWith('/marketing/')) {
      document.body.classList.add('marketing-page-active');
    } else if (location.pathname === '/time-accounting') {
      document.body.classList.add('time-accounting-page-active');
    } else if (location.pathname === '/timeclock') {
      document.body.classList.add('timeclock-page-active');
    } else if (location.pathname === '/automations') {
      document.body.classList.add('automations-page-active');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('proposals-page-active', 'bookings-page-active', 'resources-page-active', 'services-page-active', 'crm-page-active', 'customers-page-active', 'kpi-dashboard-page-active', 'tasks-page-active', 'marketing-page-active', 'time-accounting-page-active', 'timeclock-page-active', 'automations-page-active');
    };
  }, [location.pathname]);

  // Click outside to close search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchExpanded(false);
      }
    };

    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded]);

  // Function to count unviewed missed calls
  const countUnviewedMissedCalls = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('call_history')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (error) {
        console.error('Error counting unviewed missed calls:', error);
      } else {
        const missedCount = count || 0;
        setUnviewedMissedCount(missedCount);
        console.log(`📞 Dial pad icon badge: ${missedCount} unviewed missed calls`);
      }
    } catch (error) {
      console.error('Error in countUnviewedMissedCalls:', error);
    }
  };

  // Count unviewed missed calls on mount and business change
  useEffect(() => {
    if (selectedBusiness?.id) {
      countUnviewedMissedCalls();
    }
  }, [selectedBusiness?.id]);

  // Set up real-time subscription for call updates to update badge
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    const channel = supabase
      .channel(`call_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          console.log('📞 Call history changed, updating badge:', payload);
          
          // If it's an UPDATE event and the call was marked as viewed
          if (payload.eventType === 'UPDATE' && payload.new && (payload.new as any).viewed === true) {
            console.log('📞 Call marked as viewed, recounting badge');
          }
          
          // Recount when any call changes (INSERT, UPDATE, DELETE)
          countUnviewedMissedCalls();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);

  // Add effect to prevent overscroll/bounce behavior on mobile
  useEffect(() => {
    // Function to prevent default on touchmove for certain elements
    const preventBounce = (event: TouchEvent) => {
      // Check if we're at the top of the page and trying to pull down
      if (window.scrollY === 0 && event.touches[0].clientY > 0) {
        event.preventDefault();
      }
    };

    // Apply styles to prevent elastic overscroll on iOS
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    
    // Add event listeners to prevent bouncing on header
    const header = document.querySelector('header');
    if (header) {
      header.addEventListener('touchmove', preventBounce, { passive: false });
    }

    // Cleanup function
    return () => {
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
      if (header) {
        header.removeEventListener('touchmove', preventBounce);
      }
    };
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      console.log("App: Checking permissions...");
      // All access levels now hardcoded - no longer using permissions
      const calendarAccess = true;
      const proposalsAccess = 'manage' as 'manage' | 'view' | 'disabled';
      const paymentsAccess = true;
      const payrollAccess = true;
      const tasksAccess = true;
      const timeclockAccess = 'manage' as 'manage' | 'view' | 'disabled';
      const messagesAccess = true;
      const automationsAccess = 'manage' as 'manage' | 'view' | 'disabled';
      console.log("App: Messages access:", messagesAccess);
      console.log("App: Automations access:", automationsAccess);
      setHasBookings(true);
      setHasCalendar(calendarAccess);
      setProposalsAccess(proposalsAccess);
      setHasPayments(paymentsAccess);
      setHasCustomers(true);
      setHasPayroll(payrollAccess);
      setHasSettings(true);
      setTimeclockPermissionLevel('manage');
      setHasMessages(true);
    };
    checkAccess();
  }, []);

  useEffect(() => {
    const loadUserInfo = async () => {
      if (!supabase) return;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const resource = await getResourceByAuthId(user.id);
        if (resource) {
          setUserInfo({ avatarUrl: resource.avatar_url });
        }
      }
    };
    loadUserInfo();
  }, []);

  useEffect(() => {
    async function checkPermissions() {
      setLoading(false);
    }
    checkPermissions();
  }, []);

  useEffect(() => {
    const loadPermissions = async () => {
      setBookingsPermissionLevel('manage');
      setHasSettings(true);
      setSettingsPermissionLevel('manage');
    };
    loadPermissions();
  }, []);

  const handleSignOut = async () => {
    if (isSigningOut) {
      return; // Prevent multiple clicks
    }
    
    try {
      setIsSigningOut(true);
      
      // Use the comprehensive sign out function
      const { error } = await authService.signOut();

      
      if (error) {
        console.error('Sign out error:', error);
      } else {
        console.log('✅ Sign out completed successfully');
      }
      
      // Add delay to ensure console logs are visible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Redirect to login page
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Error during sign out:', error);
      // Even if there's an error, redirect to login
      window.location.href = '/login';
    } finally {
      console.log('🔍 Setting isSigningOut to false');
      setIsSigningOut(false);
    }
  };

    useEffect(() => {
    function handleClickOutside(event: Event) {
      const target = event.target as Node;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setShowMobileMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listen for warranty modal opening events
  useEffect(() => {
    const handleWarrantyModalOpen = (event: CustomEvent) => {
      console.log('🛡️ App: Opening warranty modal with data:', event.detail);
      const originalBookingId = event.detail?.originalBookingId;
      console.log('🛡️ App: Original booking ID:', originalBookingId);
      
      // Store the original booking ID for the warranty modal
      if (originalBookingId) {
        localStorage.setItem('warrantyOriginalBookingId', originalBookingId);
      }
      
      setIsCreateWarrantyModalOpen(true);
    };

    window.addEventListener('openWarrantyModal', handleWarrantyModalOpen as EventListener);
    
    return () => {
      window.removeEventListener('openWarrantyModal', handleWarrantyModalOpen as EventListener);
    };
  }, []);

  // Handle closing the sidebar when clicking on a link (mobile only)
  useEffect(() => {
    if (appContentRef.current) {
      const handleCloseSidebar = () => {
        if (isMobile) {
          setSidebarOpen(false);
        }
      };
      
      appContentRef.current.addEventListener('closeSidebar', handleCloseSidebar);
      return () => {
        appContentRef.current?.removeEventListener('closeSidebar', handleCloseSidebar);
      };
    }
  }, [isMobile, appContentRef]);

  // Toggle body scroll when sidebar is open on mobile
  useEffect(() => {
    // Only handle scroll locking on mobile
    if (!isMobile) return;
    
    const html = document.documentElement;
    const body = document.body;
    
    if (sidebarOpen) {
      // Store current scroll position as a data attribute
      html.style.setProperty('--scroll-position', `${window.scrollY}px`);
      
      // Apply scroll lock to both html and body
      html.classList.add('scroll-locked');
      body.classList.add('scroll-locked');
      body.classList.add('sidebar-open');
      
      // Set the top offset to maintain scroll position
      body.style.top = `-${window.scrollY}px`;
    } else {
      // Get stored scroll position
      const scrollY = parseFloat(html.style.getPropertyValue('--scroll-position') || '0');
      
      // Remove all scroll lock classes
      html.classList.remove('scroll-locked');
      body.classList.remove('scroll-locked');
      body.classList.remove('sidebar-open');
      
      // Reset styles and restore scroll position
      body.style.top = '';
      window.scrollTo(0, scrollY);
    }
    
    return () => {
      // Cleanup: ensure all classes and styles are removed
      html.classList.remove('scroll-locked');
      body.classList.remove('scroll-locked');
      body.classList.remove('sidebar-open');
      body.style.top = '';
    };
  }, [sidebarOpen, isMobile]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setShowMobileMenu(false);
    }
  }, [location.pathname, isMobile]);

  // Update isMobile state when window size changes
  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth < 768;
      setIsMobile(mobileView);
      
      // Only auto-open sidebar on desktop
      if (!mobileView) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  // Initialize app by running necessary setup functions
  useEffect(() => {
    const initializeApp = async () => {
      // Setup inspection photos bucket
      setupInspectionPhotosBucket();
    };
    
    initializeApp();
  }, []);

  // Task dropdown handlers removed

  // Task modal resource fetching removed

  // Removed all persistence systems
  // No need for duplicate systems here

  // PWA AUTO-REOPEN: Listen for when user returns to PWA after switching apps
  useEffect(() => {
    // Listen for custom events from centralized system
    // Removed all persistence event listeners
  }, []);

  // Initialize centralized auth state management
  // TEMPORARILY DISABLED - useAuth hook handles session management
  // useEffect(() => {
  //   const initializeAuthSystems = async () => {
  //     try {
  //       await AuthStateManager.initializeAll();
  //       console.log('🚀 App: Coordinated auth systems initialized successfully');
  //     } catch (error) {
  //       console.error('❌ App: Failed to initialize auth systems:', error);
  //     }
  //   };
  //   
  //   initializeAuthSystems();
  //   
  //   // Cleanup function
  //   return () => {
  //     AuthStateManager.cleanupAll().catch((error: unknown) => {
  //       console.error('❌ App: Error during auth cleanup:', error);
  //     });
  //   };
  // }, []);

  // Only show loading screen if we don't have a business selected yet
  // This prevents unmounting when businessLoading changes during tab switches
  if (businessLoading && !selectedBusiness) {
    return <LoadingScreen />;
  }

  // Show error screen if business context failed to load
  if (businessError && !selectedBusiness && !businessLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Connection Error
            </h2>
            <p className="text-red-600 mb-4">
              {businessError}
            </p>
            <button
              onClick={() => {
                console.log('🔄 User clicked retry - forcing business reload');
                forceLoadBusinesses();
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-100 relative overflow-x-hidden"
      ref={appContentRef}
      data-app-content
    >
      <header className="main-header bg-black backdrop-blur-xl bg-opacity-95 border-b border-white/20 px-4 h-12 flex items-center justify-between fixed top-0 left-0 right-0 w-full z-[80] shadow-lg shadow-black/20 transition-all duration-300 ease-out" style={{ 
        touchAction: 'none', 
        WebkitOverflowScrolling: 'auto',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        height: 'calc(3rem + env(safe-area-inset-top, 0px))',
        transform: 'translateZ(0)',
        willChange: 'transform',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden'
      }}>
        <div className="flex items-center space-x-1 md:space-x-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:text-white p-2 rounded-xl hover:bg-white/15 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
            aria-label="Toggle sidebar menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Global Search Bar - Mobile: Icon only, Desktop: Full bar */}
          <div className="relative">
            {/* Mobile: Search Icon Only */}
            <div className="md:hidden" ref={searchRef}>
              <button
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                className={`p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all duration-200 relative z-[60] ${
                  isSearchExpanded ? 'text-white bg-gray-800/50' : ''
                }`}
              >
                <Search className="h-5 w-5" />
              </button>
              
              {/* Expanded Search Bar for Mobile */}
              <div className={`absolute top-0 left-0 transition-all duration-300 ease-out z-50 ${
                isSearchExpanded 
                  ? 'w-64 opacity-100 scale-100' 
                  : 'w-10 opacity-0 scale-95 pointer-events-none'
              }`}>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full pl-12 pr-10 py-2 bg-gray-800/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                    autoFocus={isSearchExpanded}
                  />
                  <button
                    onClick={() => setIsSearchExpanded(false)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop: Full Search Bar */}
            <div className="hidden md:block w-96">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                />
              </div>
            </div>
          </div>


          {/* Nexus AI Assistant Button - Temporarily removed from mobile */}
          {/* {isMobile && (
            <button
              onClick={() => setIsNexusOverlayOpen(true)}
              className="text-white hover:text-white transition-all duration-300 ease-out p-2 rounded-xl transform hover:scale-105 active:scale-95 touch-manipulation shadow-lg border border-black"
              title="Nexus AI Assistant"
              style={{
                background: 'linear-gradient(135deg, #000000 0%, #2d1b69 60%, #7c3aed 100%)',
                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.4)'
              }}
            >
              <Bot className="h-5 w-5" />
            </button>
          )} */}

          {/* Top Navigation - Now empty, reserved for future additions */}
          <nav className="hidden md:flex items-center space-x-6">
            {/* Navigation links moved to left sidebar - keeping this space for future additions */}
          </nav>

          {/* Mobile Navigation - REMOVE, now handled by LeftNavigation */}
          {/* <div className="md:hidden">
            <div className="relative" ref={mobileMenuRef}>
              <div>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="text-gray-300 hover:text-white flex items-center space-x-1.5 px-1.5 py-1.5"
                >
                  <span className="text-sm font-medium">
                      ? "Calendar"
                      : location.pathname === "/bookings"
                      ? "Bookings"
                      : location.pathname === "/proposals"
                      ? "Proposals"
                      : location.pathname === "/payments"
                      ? "Payments"
                      : location.pathname === "/tasks"
                      ? "Tasks"
                      : location.pathname === "/gps"
                      ? "GPS"
                      : "Menu"}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      showMobileMenu ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showMobileMenu && (
                  <div className="mobile-menu absolute top-full left-1/2 -translate-x-1/2 w-32 transform transition-all duration-200 ease-out bg-white rounded-lg shadow-lg border border-gray-200">
                    {hasCalendar && (
                      <Link
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Calendar
                      </Link>
                    )}
                    {hasBookings && (
                      <Link
                        to="/bookings"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Bookings
                      </Link>
                    )}
                    {proposalsAccess !== "disabled" && (
                      <Link
                        to="/proposals"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Proposals
                      </Link>
                    )}
                    {hasPayments && (
                      <Link
                        to="/payments"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Payments
                      </Link>
                    )}
                    <Link
                      to="/tasks"
                      className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      Tasks <span className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded ml-1">Beta</span>
                    </Link>
                    <Link
                      to="/gps"
                      className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      GPS
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div> */}
        </div>

        <div className="flex items-center space-x-2">
          {/* Dial Pad Icon - Hidden when search is expanded on mobile only */}
          {(!isSearchExpanded || window.innerWidth >= 768) && (
            <Link
              to="/calls-beta"
              className="relative flex items-center space-x-2 text-white/90 hover:text-white transition-all duration-300 ease-out p-2 rounded-xl hover:bg-white/15 transform hover:scale-105 active:scale-95 touch-manipulation"
              title="Dial Pad"
            >
            <div className="w-5 h-5 grid grid-cols-3 gap-0.5">
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
            </div>
            
              {/* Badge count for unviewed missed calls */}
              {unviewedMissedCount > 0 && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium shadow-lg">
                  {unviewedMissedCount > 9 ? '9+' : unviewedMissedCount}
                </span>
              )}
            </Link>
          )}

          {/* Tasks Quick View Dropdown - Moved to right side */}
          <TasksDropdown />
          
          {/* Business Profile Dropdown */}
          <BusinessProfileDropdown />
          
          {/* Sign Out Button - Temporarily removed */}
          {/* <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            className={`flex items-center space-x-2 text-white/90 hover:text-white transition-all duration-300 ease-out p-2 rounded-xl hover:bg-white/15 transform hover:scale-105 active:scale-95 touch-manipulation ${
              isSigningOut ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={isSigningOut ? "Signing out..." : "Sign out"}
          >
            {isSigningOut ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <LogOut className="h-4 w-4" />
            )}
            <span className="text-sm font-medium hidden sm:inline">
              {isSigningOut ? "Signing out..." : "Sign out"}
            </span>
          </button> */}
        </div>
      </header>

      <div className="flex h-[calc(100vh-3rem)] mt-12" style={{ marginTop: 'calc(3rem + env(safe-area-inset-top, 0px))' }}>
        {/* Backdrop overlay for mobile - only show for non-PWA mode since PWA uses CSS backdrop */}
        {sidebarOpen && isMobile && !document.body.classList.contains('pwa-mode') && (
          <div
            className="fixed inset-0 bg-transparent z-[75] transition-opacity duration-300 ease-in-out"
            onClick={() => setSidebarOpen(false)}
            onTouchMove={(e) => e.preventDefault()}
            onWheel={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            style={{ 
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              pointerEvents: 'auto',
              overscrollBehavior: 'none',
              left: '280px', // Updated to match PWA sidebar width
              width: 'calc(100% - 280px)' // Only cover the content area, not the sidebar
            }}
            aria-hidden="true"
          />
        )}

        {/* PWA mode backdrop overlay - covers entire screen for click-outside functionality */}
        {sidebarOpen && isMobile && document.body.classList.contains('pwa-mode') && (
          <div
            className="fixed inset-0 z-[1099]" // Just below sidebar z-index (1100) but covers full screen
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSidebarOpen(false);
            }}
            onTouchMove={(e) => e.preventDefault()}
            onWheel={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            style={{ 
              pointerEvents: 'auto',
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              overscrollBehavior: 'none',
              backgroundColor: 'transparent'
            }}
            aria-hidden="true"
          />
        )}

        {/* Sidebar - Update z-index and height, no need for extra padding since we removed the bottom nav */}
        <div
          className={`fixed ${
            sidebarOpen ? "w-[220px]" : "w-0"
          } h-[calc(100vh-3rem)] bg-black border-r border-gray-700 transition-all duration-300 z-[1100] overflow-y-auto md:flex md:flex-col md:min-h-full ${
            sidebarOpen && isMobile ? "overflow-y-auto" : "overflow-hidden"
          }`}
          style={{
            top: 'calc(3rem + env(safe-area-inset-top, 0px))', // Position below the enhanced header
            ...(isMobile ? {
              height: 'calc(100vh - 3rem - env(safe-area-inset-top, 0px))',
              maxHeight: 'calc(100vh - 3rem - env(safe-area-inset-top, 0px))',
              bottom: '0',
              paddingBottom: '20px', // Reduced padding since no bottom nav
              zIndex: 1100,
              pointerEvents: 'auto'
            } : {})
          }}
        >
          <LeftNavigation
            hasSettings={hasSettings}
            isDevelopment={isDevelopment}
            timeclockPermissionLevel={timeclockPermissionLevel}
            hasCalendar={hasCalendar}
            hasBookings={hasBookings}
            proposalsAccess={proposalsAccess}
            hasPayments={hasPayments}
            hasCustomers={hasCustomers}
            hasMessages={hasMessages}
            hasTasks={shouldShowPage('tasks', canAccess.tasks)}
            isMobile={isMobile}
            bottomNavItems={bottomNavItems}
            sidebarOpen={sidebarOpen}
          />
        </div>

        {/* Main content area - adjust padding at the bottom, no longer need extra for the bottom nav */}
        <div className={`flex-1 transition-all duration-300 opacity-100`}
          style={{
            pointerEvents: isMobile && sidebarOpen ? 'none' : 'auto',
            width: sidebarOpen && !isMobile ? 'calc(100% - 220px)' : '100%', // Reduce width by sidebar width on desktop (220px)
            marginLeft: sidebarOpen && !isMobile ? '220px' : '0', // Add margin to push content right
            transition: 'width 0.3s, margin-left 0.3s' // Smooth transition for width changes
          }}
        >
                <main 
                  className={`main-content ${location.pathname === '/crm' || location.pathname === '/customers' || location.pathname === '/services' ? 'bg-white' : 'bg-gray-100'} ${location.pathname !== '/tasks' && location.pathname !== '/calendarV2' && location.pathname !== '/mobile-calendar' && location.pathname !== '/messages' && location.pathname !== '/time-accounting' && location.pathname !== '/timeclock' && location.pathname !== '/marketing' && location.pathname !== '/kpi-dashboard' && location.pathname !== '/crm' && location.pathname !== '/customers' && location.pathname !== '/services' && location.pathname !== '/proposals' && location.pathname !== '/bookings' && location.pathname !== '/resources' && location.pathname !== '/automations' && location.pathname !== '/stock' && location.pathname !== '/sell-products' && location.pathname !== '/online-store' ? 'p-4 md:p-6' : 'p-0'} ${isMobile && location.pathname !== '/time-accounting' && location.pathname !== '/timeclock' && location.pathname !== '/marketing' && location.pathname !== '/kpi-dashboard' && location.pathname !== '/messages' && location.pathname !== '/crm' && location.pathname !== '/customers' && location.pathname !== '/services' && location.pathname !== '/proposals' && location.pathname !== '/bookings' && location.pathname !== '/resources' && location.pathname !== '/automations' && location.pathname !== '/stock' && location.pathname !== '/sell-products' && location.pathname !== '/online-store' ? 'pb-4' : ''} ${location.pathname === '/calendarV2' ? 'calendar-v2-page' : ''} ${location.pathname === '/proposals' ? 'proposals-page' : ''} ${location.pathname === '/bookings' ? 'bookings-page' : ''} ${location.pathname === '/resources' ? 'resources-page' : ''} ${location.pathname === '/services' ? 'services-page' : ''} ${location.pathname === '/crm' ? 'crm-page' : ''} ${location.pathname === '/customers' ? 'customers-page' : ''} ${location.pathname === '/kpi-dashboard' ? 'kpi-dashboard-page' : ''} ${location.pathname === '/tasks' ? 'tasks-page' : ''} ${(location.pathname === '/marketing' || location.pathname.startsWith('/marketing/')) ? 'marketing-page' : ''} ${location.pathname === '/time-accounting' ? 'time-accounting-page' : ''} ${location.pathname === '/timeclock' ? 'timeclock-page' : ''} ${location.pathname === '/automations' ? 'automations-page' : ''} ${location.pathname === '/online-store' ? 'online-store-page' : ''} ${(location.pathname.startsWith('/settings') || location.pathname.startsWith('/billing')) ? 'settings-page' : ''}`}
                  data-page={
                    location.pathname === '/time-accounting' ? 'time-accounting' : 
                    location.pathname === '/timeclock' ? 'timeclock' : 
                    location.pathname === '/messages' ? 'messages' :
                    location.pathname === '/automations' ? 'automations' :
                    undefined
                  }
                >
            <Outlet context={{ tasksPermissionLevel: "manage" as const, automationsPermissionLevel: "manage" as const, settingsPermissionLevel }} />
          </main>
        </div>
      </div>

      {/* Mobile Bottom Navigation - only visible on mobile, but not PWA */}
      {isMobile && !isPWA && (
        <MobileBottomNavigation
          hasCalendar={hasCalendar}
          hasBookings={hasBookings}
          proposalsAccess={proposalsAccess}
          hasPayments={hasPayments}
          hasCustomers={hasCustomers}
          permissionLevel={bookingsPermissionLevel}
          openCreateBookingModal={() => setIsCreateBookingModalOpen(true)}
          openCreateProposalModal={() => setIsCreateProposalModalOpen(true)}
          openPaymentModal={() => setIsPaymentModalOpen(true)}
          openWithdrawStockModal={() => setIsWithdrawStockModalOpen(true)}
          hasSettings={hasSettings}
          hasMessages={hasMessages}
          onNavItemsChange={setBottomNavItems}
          openCreateMobileBookingModal={() => setIsCreateMobileBookingModalOpen(true)}
        />
      )}

      {/* Show FloatingActionButton on all platforms - unified experience */}
      <FloatingActionButton
        permissionLevel={bookingsPermissionLevel}
      />
      
      {/* Modals - moved outside of FloatingActionButton */}

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateBookingModal, clearing localStorage');
          // clearModalData(); // Clear modal persistence data - REMOVED
          setIsCreateBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateBookingModal success, clearing localStorage');
          // clearModalData(); // Clear modal persistence data - REMOVED
          setIsCreateBookingModalOpen(false);
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async () => {
          setIsCreateProposalModalOpen(false);
        }}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          setIsWithdrawStockModalOpen(false);
        }}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateMobileBookingModal, clearing localStorage');
          // clearMobileModalData(); // Clear modal persistence data - REMOVED
          setIsCreateMobileBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateMobileBookingModal success, clearing localStorage');
          // clearMobileModalData(); // Clear modal persistence data - REMOVED
          setIsCreateMobileBookingModalOpen(false);
          
          // Dispatch custom event to refresh mobile calendar
          window.dispatchEvent(new CustomEvent('mobileBookingCreated'));
        }}
      />

      {/* Warranty Booking Modal */}
      <CreateBookingModal
        isOpen={isCreateWarrantyModalOpen}
        onClose={() => {
          console.log('🛡️ User manually closed WarrantyModal');
          setIsCreateWarrantyModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('🛡️ WarrantyModal success');
          setIsCreateWarrantyModalOpen(false);
          
          // Dispatch custom event to refresh bookings
                  window.dispatchEvent(new CustomEvent('warrantyBookingCreated'));
      }}
      isWarrantyBooking={true}
    />

    {/* Nexus AI Assistant Overlay */}
    <NexusOverlay 
      isOpen={isNexusOverlayOpen} 
      onClose={() => setIsNexusOverlayOpen(false)}
    />

  </div>
  );
});

function AuthRedirectHandler() {
  console.log('🔥 AuthRedirectHandler mounting');
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');

  // Initialize navigation service
  useEffect(() => {
    navigationService.setNavigateFunction(navigate);
  }, [navigate]);

  useEffect(() => {
    // CRITICAL: Check if we should skip the initializing screen (from enhanced login)
    const skipInitializing = localStorage.getItem('skipInitializing') === 'true';
    const preloadedRoute = localStorage.getItem('preloadedRoute');
    
    if (skipInitializing && preloadedRoute) {
      console.log('🚀 AuthRedirectHandler: Skipping initialization, navigating to preloaded route:', preloadedRoute);
      // Clear the flags
      localStorage.removeItem('skipInitializing');
      localStorage.removeItem('preloadedRoute');
      // Navigate immediately without any loading delay
      navigate(preloadedRoute, { replace: true });
      return;
    }

    // Check for cached user session to skip loading for return users
    const cachedSession = localStorage.getItem('cachedUserSession');
    if (cachedSession) {
      try {
        const { timestamp } = JSON.parse(cachedSession);
        const cacheAge = Date.now() - timestamp;
        
        // Tiered caching strategy for security:
        // - 5 minutes: Full cached access (for active users)
        // - 24 hours: UI-only access with background verification (for returning users)
        // - Beyond 24 hours: Full session check required
        
        if (cacheAge < 24 * 60 * 60 * 1000) { // 24 hours instead of 15 minutes
          console.log('🚀 AuthRedirectHandler: Using cached session, skipping loading screen');
          navigate('/calendarV2', { replace: true }); // Fast path to default
          return;
        }
      } catch (e) {
        console.log('AuthRedirectHandler: Invalid cached session');
      }
    }

    // Original loading logic ONLY for truly new users
    const handleRedirect = async () => {
      // MINIMAL LOADING TIME for new users only
      const startTime = Date.now();
      const minLoadingTime = 800; // Reduced from 2000ms to 800ms

      try {
        // Check if there's a code parameter in the URL (from password reset email)
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const type = params.get('type');
        
        setDebugInfo(prev => `${prev}\n\nChecking URL params:\ncode: ${code || 'none'}\ntype: ${type || 'none'}`);
    
        if (code) {
          setIsLoading(false);
          console.log('✉️ Auth code detected, redirecting to password update page');
          setDebugInfo(prev => `${prev}\n\nAuth code detected - handling password reset`);
      
          // For password reset, navigate directly (no delay needed)
          if (type === 'recovery' || !type) {
            navigate(`/reset-password/update${location.search}`, { replace: true });
            return;
          }
        }

        // No auth code, we need to determine default route based on settings
        console.log('🔍 Determining default route from database settings');
        
        if (!selectedBusiness?.id || !supabase) {
          console.log('⚠️ No business ID or database connection');
          setDebugInfo(prev => `${prev}\n\nNo business/DB connection`);
          
          // Calculate remaining loading time to ensure minimum display time
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            navigate('/calendarV2', { replace: true }); // Default to location calendar
            setIsLoading(false);
          }, remainingTime);
          return;
        }
        
        try {
          setDebugInfo(prev => `${prev}\n\nQuerying database for business ID: ${selectedBusiness.id}`);
          
          // Try direct RPC first for better reliability 
          try {
            const { data: directData, error: directError } = await supabase.rpc(
              'get_home_screen_direct',
              { business_id_param: selectedBusiness.id }
            );
            
            console.log("Direct query result:", directData, directError);
            setDebugInfo(prev => `${prev}\n\nDirect query: ${JSON.stringify(directData || {})}`);
            
            if (!directError && directData && directData.home_screen) {
              // Calculate remaining loading time to ensure minimum display time
              const elapsedTime = Date.now() - startTime;
              const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
              
              setTimeout(() => {
                if (directData.home_screen === 'mobile_calendar') {
                  console.log("Going to mobile calendar");
                  navigate('/mobile-calendar', { replace: true });
                } else {
                  console.log("Going to location calendar");
                  navigate('/calendarV2', { replace: true });
                }
                setIsLoading(false);
              }, remainingTime);
              return;
            }
          } catch (error) {
            console.log("Direct query failed, trying regular query:", error);
            setDebugInfo(prev => `${prev}\n\nDirect query failed, trying regular query...`);
          }
          
          // Fallback to regular table query
          const { data, error } = await supabase
            .from('application_settings')
            .select('*')
            .eq('business_id', selectedBusiness.id);
            
          console.log("Regular query result:", data, error);
          setDebugInfo(prev => `${prev}\n\nRegular query: ${JSON.stringify(data || [])}`);
          
          // Calculate remaining loading time to ensure minimum display time
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            if (error || !data || data.length === 0) {
              console.log("No settings found or error, using default");
              navigate('/calendarV2', { replace: true }); // Default to location calendar
            } else {
              const setting = data[0];
              console.log(`Found setting: ${JSON.stringify(setting)}`);
              
              if (setting.home_screen === 'mobile_calendar') {
                console.log("Going to mobile calendar");
                navigate('/mobile-calendar', { replace: true });
              } else {
                console.log("Going to location calendar");
                navigate('/calendarV2', { replace: true });
              }
            }
            setIsLoading(false);
          }, remainingTime);
        } catch (error: any) {
          console.error('Error during database query:', error);
          setDebugInfo(prev => `${prev}\n\nError: ${error?.message || JSON.stringify(error)}`);
          
          // Calculate remaining loading time to ensure minimum display time
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            navigate('/calendarV2', { replace: true }); // Default to location calendar
            setIsLoading(false);
          }, remainingTime);
        }
      } catch (error: any) {
        console.error('Error in AuthRedirectHandler:', error);
        setDebugInfo(prev => `${prev}\n\nGlobal error: ${error?.message || JSON.stringify(error)}`);
        
        // Calculate remaining loading time to ensure minimum display time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          navigate('/calendarV2', { replace: true }); // Default to location calendar
          setIsLoading(false);
        }, remainingTime);
      }
    };
    
    handleRedirect();
  }, [location, navigate, selectedBusiness?.id]);
  
  // Show loading screen ONLY for truly new users
  if (isLoading) {
    return <LoadingScreen />;
  }

  // If not loading, this component has already navigated away
  return null;
}

// Custom DefaultRoute component that redirects based on application settings
const DefaultRoute = () => {
  console.log('🔥 DefaultRoute component mounting');
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [route, setRoute] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');
  const [isLoading, setIsLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  
  // If we're already on a specific route (not root), don't show loading
  const isOnSpecificRoute = location.pathname !== '/' && location.pathname !== '';
  
  useEffect(() => {
    if (isOnSpecificRoute) {
      console.log('🚀 DefaultRoute: Already on specific route, skipping initialization:', location.pathname);
      setIsLoading(false);
      return;
    }
  }, [isOnSpecificRoute, location.pathname]);
  
  useEffect(() => {
    // CRITICAL: Check if we should skip the initializing screen (from enhanced login)
    const skipInitializing = localStorage.getItem('skipInitializing') === 'true';
    const preloadedRoute = localStorage.getItem('preloadedRoute');
    
    if (skipInitializing && preloadedRoute) {
      console.log('🚀 DefaultRoute: Skipping initialization, using preloaded route:', preloadedRoute);
      // Clear the flags
      localStorage.removeItem('skipInitializing');
      localStorage.removeItem('preloadedRoute');
      // Set route immediately
      setRoute(preloadedRoute);
      setIsLoading(false);
      return;
    }

    // Check for cached user session to skip loading for return users
    const cachedSession = localStorage.getItem('cachedUserSession');
    if (cachedSession) {
      try {
        const { timestamp } = JSON.parse(cachedSession);
        const cacheAge = Date.now() - timestamp;
        
        // Tiered caching strategy for security:
        // - 5 minutes: Full cached access (for active users)
        // - 24 hours: UI-only access with background verification (for returning users)
        // - Beyond 24 hours: Full session check required
        
        if (cacheAge < 24 * 60 * 60 * 1000) { // 24 hours instead of 15 minutes
          console.log('🚀 DefaultRoute: Using cached session for instant access');
          setRoute('/calendarV2'); // Fast default route
          setIsLoading(false);
          return;
        }
      } catch (e) {
        console.log('DefaultRoute: Invalid cached session');
      }
    }

    // Original database query logic ONLY for truly new users
    // MINIMAL LOADING TIME for new users only
    const startTime = Date.now();
    const minLoadingTime = 800; // Reduced from 2000ms

    const directDatabaseQuery = async () => {
      if (!selectedBusiness?.id) {
        console.log("DefaultRoute: No business selected, using default");
        setDebugInfo('No business selected, using default route');
        
        // Calculate remaining loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          setRoute('/calendarV2'); // Default
          setIsLoading(false);
        }, remainingTime);
        return;
      }

      try {
        setDebugInfo(prev => `${prev}\n\nQuerying for business: ${selectedBusiness.id}`);
        
        // Try direct RPC first for better reliability
        try {
          if (!supabase) {
            throw new Error('Supabase client not initialized');
          }
          
          const { data: directData, error: directError } = await supabase.rpc(
            'get_home_screen_direct',
            { business_id_param: selectedBusiness.id }
          );
          
          console.log("DefaultRoute direct query result:", directData, directError);
          setDebugInfo(prev => `${prev}\n\nDirect query: ${JSON.stringify(directData || {})}`);
          
          if (!directError && directData && directData.home_screen) {
            // Calculate remaining loading time
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
              if (directData.home_screen === 'mobile_calendar') {
                console.log("DefaultRoute: Going to mobile calendar");
                setRoute('/mobile-calendar');
              } else {
                console.log("DefaultRoute: Going to location calendar");
                setRoute('/calendarV2');
              }
              setIsLoading(false);
            }, remainingTime);
            return;
          }
        } catch (error) {
          console.log("DefaultRoute direct query failed:", error);
          setDebugInfo(prev => `${prev}\n\nDirect query failed`);
        }
        
        // Fallback to regular table query
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        
        const { data, error } = await supabase
          .from('application_settings')
          .select('*')
          .eq('business_id', selectedBusiness.id);
          
        console.log("DefaultRoute regular query result:", data, error);
        setDebugInfo(prev => `${prev}\n\nRegular query: ${JSON.stringify(data || [])}`);
        
        // Calculate remaining loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          if (error || !data || data.length === 0) {
            console.log("DefaultRoute: No settings found or error, using default");
            setRoute('/calendarV2'); // Default to location calendar
          } else {
            const setting = data[0];
            console.log(`DefaultRoute: Found setting: ${JSON.stringify(setting)}`);
            
            if (setting.home_screen === 'mobile_calendar') {
              console.log("DefaultRoute: Going to mobile calendar");
              setRoute('/mobile-calendar');
            } else {
              console.log("DefaultRoute: Going to location calendar");
              setRoute('/calendarV2');
            }
          }
          setIsLoading(false);
        }, remainingTime);
      } catch (error: any) {
        console.error('Error in DefaultRoute:', error);
        setDebugInfo(prev => `${prev}\n\nError: ${error?.message || JSON.stringify(error)}`);
        
        // Calculate remaining loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          setRoute('/calendarV2'); // Default to location calendar
          setIsLoading(false);
        }, remainingTime);
      }
    };
    
    directDatabaseQuery();
  }, [selectedBusiness?.id]);
  
  // Show loading screen ONLY for truly new users without cached sessions
  if (isLoading || !route) {
    return <LoadingScreen />;
  }
  
  // Only navigate once we're fully loaded and know our destination
  return <Navigate to={route} replace />;
};

// Add route navigation helper to prevent hard refresh issues
function useRoutePreloader() {
  useEffect(() => {
    // Preload feedback route to prevent loading issues
    const preloadRoute = () => {
      // Force React Router to recognize all routes
      if (typeof window !== 'undefined') {
        // Listen for popstate events (back/forward button)
        const handlePopState = (e: PopStateEvent) => {
          // Force a re-render if navigating to feedback routes
          if (window.location.pathname.startsWith('/feedback/')) {
            // Small delay to ensure React Router processes the route
            setTimeout(() => {
              if (window.location.pathname.startsWith('/feedback/')) {
                // The route should be handled by React Router
                console.log('Feedback route accessed:', window.location.pathname);
              }
            }, 50);
          }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
      }
    };

    preloadRoute();
  }, []);
}

// Shared constant for public routes that don't need auth systems
const PUBLIC_ROUTES = [
  '/login',
  '/signup', 
  '/setup',
  '/reset-password',
  '/reset-password/update',
  '/feedback',
  '/liability-waiver',
  '/receipt',
  '/proposal',
  '/book',
  '/admin-subscription-management',
  '/resource-subscription-expired',
  '/subscription-success'
];

// Helper function to check if a route is public
const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some(route => 
    pathname.startsWith(route) || 
    pathname === route ||
    pathname.match(/^\/[^\/]+\/book$/) || // businessSlug/book
    pathname.match(/^\/[^\/]+\/store$/) || // businessSlug/store
    pathname.match(/^\/[^\/]+\/booking-success$/) || // businessSlug/booking-success
    pathname.match(/^\/[^\/]+\/[^\/]+$/) // businessSlug/formSlug
  );
};

// Component that only initializes auth systems for authenticated users
const AuthenticatedAppInitializer = () => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);
  
  // TEMPORARILY DISABLED - useAuth hook handles session management
  // useEffect(() => {
  //   if (isPublic) {
  //     console.log('🔒 Public route detected, skipping auth system initialization');
  //     return;
  //   }
  //   
  //   const initializeAuthSystems = async () => {
  //     try {
  //       console.log('🚀 Initializing auth systems for authenticated route...');
  //       await AuthStateManager.initializeAll();
  //       console.log('🚀 App: Coordinated auth systems initialized successfully');
  //     } catch (error) {
  //       console.error('❌ App: Failed to initialize auth systems:', error);
  //     }
  //   };
  //   
  //   initializeAuthSystems();
  //   
  //   // Cleanup function
  //   return () => {
  //     if (!isPublic) {
  //       AuthStateManager.cleanupAll().catch((error: unknown) => {
  //         console.error('❌ App: Error during auth cleanup:', error);
  //       });
  //     }
  //   };
  // }, [isPublic]);
  
  // Only run migrations for authenticated routes
  useEffect(() => {
    if (isPublic) {
      return;
    }
    
    const runMigrations = async () => {
      try {
        console.log('🔄 Running migrations for authenticated user...');
        await migrateLocalStorageToDatabase();
      } catch (error) {
        console.error('Error running migrations:', error);
      }
    };
    
    runMigrations().catch(error => {
      console.error('Failed to run migrations:', error);
    });
  }, [isPublic]);

  // Handle business cleanup on logout (ONLY for logout, not tab switches)
  useEffect(() => {
    if (isPublic) {
      return;
    }

    const cleanupOnLogout = (event: any, session: any) => {
      // ONLY clear business data on actual logout (SIGNED_OUT)
      if (event === 'SIGNED_OUT') {
        console.log('🧹 App: User signed out - clearing business localStorage');
        // Clear any business-related localStorage without forcing re-renders
        localStorage.removeItem('selectedBusiness');
        localStorage.removeItem('businesses');
        localStorage.removeItem('usePreloadedBusinessData');
      }
      // Ignore all other events (INITIAL_SESSION, TOKEN_REFRESHED, etc.)
    };

    const unsubscribe = authStateManager.subscribe(cleanupOnLogout);
    
    return () => unsubscribe();
  }, [isPublic]);
  
  return null;
};

// Component that conditionally renders the reminder processor
const ConditionalReminderProcessor = () => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);
  
  // Only call the hook for authenticated routes
  if (!isPublic) {
    useReminderProcessor();
  }
  
  return null;
};

const App = () => {
  // Call our hook to enable auto-selection for all number inputs
  useSelectInputOnFocus();
  
  // Global booking modal state for instant opening
  const [globalBookingModal, setGlobalBookingModal] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    booking: any;
    isLoading: boolean;
    targetPage: string | null;
  }>({
    isOpen: false,
    bookingId: null,
    booking: null,
    isLoading: false,
    targetPage: null
  });
  
  // Initialize mobile service for native app features
  useEffect(() => {
    mobileService.initialize();
  }, []);

  // Global message listener for push notifications
  useEffect(() => {
    console.log('🔔 [GLOBAL] Setting up global message listener for push notifications');

    import('./lib/supabaseClient').then(({ supabase }) => {
      if (!supabase) {
        console.warn('🔔 [GLOBAL] Supabase client not available');
        return;
      }
      
      console.log('🔔 [GLOBAL] Supabase client loaded successfully');

      // Listen to all new messages globally
      const subscription = supabase
        .channel('global_messages_push_notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'all_messages'
          },
          (payload) => {
            console.log('🔔 [GLOBAL] New message detected globally:', payload);
            
            const newMessage = payload.new;
            
            // Only send notifications for inbound messages
            if (newMessage.direction === 'inbound') {
              console.log('🔔 [GLOBAL] Processing inbound message for push notification:', {
                id: newMessage.id,
                direction: newMessage.direction,
                businessId: newMessage.business_id,
                messageType: newMessage.message_type
              });

              const customerName = newMessage.first_name && newMessage.last_name 
                ? `${newMessage.first_name} ${newMessage.last_name}`
                : 'Customer';
              
              const channelType = newMessage.message_type === 'email' ? 'email' : 'sms';
              const messagePreview = newMessage.content || newMessage.body || 'New message received';
              
              console.log('🔔 [GLOBAL] Sending push notification:', {
                businessId: newMessage.business_id,
                customerName,
                channelType,
                messagePreview: messagePreview.substring(0, 50) + '...'
              });

              // Send push notification
              notifyNewMessage(
                newMessage.business_id,
                newMessage.customer_id || newMessage.communication_id || '',
                customerName,
                messagePreview,
                channelType
              ).then(success => {
                console.log('🔔 [GLOBAL] Push notification result:', success ? 'SUCCESS' : 'FAILED');
              }).catch(error => {
                console.error('🔔 [GLOBAL] Failed to send push notification:', error);
              });
            } else {
              console.log('🔔 [GLOBAL] Skipping outbound message:', newMessage.direction);
            }
          }
        )
        .subscribe((status) => {
          console.log('🔔 [GLOBAL] Global message subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('🔔 [GLOBAL] ✅ Successfully subscribed to message changes');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('🔔 [GLOBAL] ❌ Channel error - subscription failed');
          } else if (status === 'TIMED_OUT') {
            console.error('🔔 [GLOBAL] ❌ Subscription timed out');
          } else if (status === 'CLOSED') {
            console.log('🔔 [GLOBAL] 🔒 Subscription closed');
          }
        });

      return () => {
        console.log('🔔 [GLOBAL] Cleaning up global message subscription');
        subscription.unsubscribe();
      };
    }).catch(error => {
      console.error('🔔 [GLOBAL] Failed to set up global message listener:', error);
    });
  }, []);

  // Removed all persistence systems
  
  // Version check to ensure users always have the latest version
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        const data = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        if (currentVersion && currentVersion !== data.version) {
          // New version detected, clear cache and reload
          console.log(`New version detected: ${data.version} (current: ${currentVersion})`);
          if ("caches" in window) {
            caches.keys().then((names) => {
              names.forEach((name) => {
                caches.delete(name);
              });
            });
          }
          localStorage.setItem("app-version", data.version);
          window.location.reload();
        } else if (!currentVersion) {
          localStorage.setItem("app-version", data.version);
        }
      } catch (error) {
        console.error("Version check failed:", error);
      }
    };

    // Initial check
    checkVersion();
    
    // Check every 5 minutes (300000ms) - more frequent than before for better UX
    const interval = setInterval(checkVersion, 300000);

    return () => clearInterval(interval);
  }, []);

  // Removed migrations from here - moved to AuthenticatedAppInitializer

  // First, let's check if there's a toast container div in the document
  if (!document.getElementById('toast-container')) {
    const container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
  }

  // Add route navigation helper to prevent hard refresh issues
  useRoutePreloader();

  return (
    <>
    <ImpersonationBanner />
    <SupabaseProvider>
        <BusinessProvider business={null}>
          <DataCacheProvider>
            <ApplicationSettingsProvider>
              <PermissionProvider>
                <BookingStatus.Provider>
                  <NotificationsProvider>
                    <SubscriptionProvider>
                      <UnreadMessagesProvider>
                        <UnreadOpportunitiesProvider>
                          <TwilioMessagePollerProvider pollingInterval={5000}>
                          <CallProvider>
                            <SimpleTimerProvider>
                              <BrowserRouter>
                                <ToastProvider>
                                  {/* Initialize auth systems only for authenticated routes */}
                                <AuthenticatedAppInitializer />
                                <ConditionalReminderProcessor />
                                <Routes>
                                  {/* Public Routes - These must come FIRST to ensure they're not caught by auth logic */}
                                  <Route path="/login" element={<Login />} />
                                  <Route path="/signup" element={<Signup />} />
                                  <Route path="/setup" element={<Setup />} />
                                  <Route path="/reset-password" element={<ResetPassword />} />
                                  <Route path="/reset-password/update" element={<UpdatePassword />} />
                                  <Route path="/feedback/:token" element={<FeedbackPage />} />
                                  <Route path="/liability-waiver/:token" element={<LiabilityWaiver />} />

                                  <Route
                                    path="/receipt/:id"
                                    element={<PublicPDFReceipt />}
                                  />
                                  {/* CRITICAL: Public proposal route - must be BEFORE any wildcards */}
                                  <Route
                                    path="/proposal/:token"
                                    element={<PublicProposal />}
                                  />
                                  <Route path="/:businessSlug/book" element={<BookOnline />} />
                                  <Route path="/:businessSlug/booking-success" element={<BookingSuccess />} />
                                  <Route path="/:businessSlug/:formSlug" element={<PublicLeadForm />} />
                                  <Route path="/pay-invoice/:token" element={<PayInvoice />} />
                                  <Route path="/:businessSlug/store" element={<StoreOnline />} />
                                  <Route path="/book" element={<Navigate to="/" replace />} />
                                  
                                                                           {/* Subscription Management Routes */}
                                         <Route path="/admin-subscription-management" element={<AdminSubscriptionManagement />} />
                                         <Route path="/resource-subscription-expired" element={<ResourceSubscriptionExpired />} />
                                         <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                                  
                                  {/* Protected Routes */}
                                  <Route
                                    element={
                                      <AuthGuard>
                                          <AppContent />
                                      </AuthGuard>
                                    }
                                  >
                                    {/* Root route - now properly handled within PrivateRoute */}
                                    <Route index element={<DefaultRoute />} />
                                    <Route path="/" element={<DefaultRoute />} />
                                    <Route path="/bookings" element={
                                      <PermissionGuard permission="bookings.pageAccess">
                                        <Bookings />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/proposals" element={
                                      <PermissionGuard permission="proposals.pageAccess">
                                        <Proposals />
                                      </PermissionGuard>
                                    } />
                                    {/* Payments route disabled temporarily - keeping code for future use */}
                                    {/* <Route path="/payments" element={<Payments />} /> */}
                                    <Route path="/customers" element={
                                      <PermissionGuard permission="customers.pageAccess">
                                        <Customers />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/payroll" element={
                                      <PermissionGuard permission="payroll.pageAccess">
                                        <ManagePayroll />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/stock" element={<Stock />} />
                                    <Route path="/sell-products" element={<SellProducts />} />
                                    <Route path="/online-store" element={<OnlineStore />} />
                                    <Route path="/app-store" element={
                                      <ErrorBoundary>
                                        <AppStore />
                                      </ErrorBoundary>
                                    } />
                                    <Route path="/resources" element={<Resources />} />
                                    <Route path="/services" element={<Services />} />
                                    <Route path="/reports" element={<Reports />}>
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                    </Route>
                                    <Route path="/settings/*" element={<Settings />} />
                                    <Route path="/marketing" element={
                                      <PermissionGuard permission="marketing.pageAccess" pageId="marketing">
                                        <Marketing />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/time-accounting" element={
                                      <PermissionGuard permission="timeAccounting.pageAccess">
                                        <TimeAccounting />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/workflows" element={<Workflows />} />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route
                                      path="/sales-reports"
                                      element={<SalesReports />}
                                    >
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                      {/* <Route path="cash-flow" element={<CashFlow />} /> */}
                                    </Route>
                                    <Route path="/team-reports" element={<TeamReports />} />
                                    <Route path="/tasks" element={
                                      <PermissionGuard permission="tasks.pageAccess" pageId="tasks">
                                        <Tasks />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/timeclock" element={<Timeclock />} />
                                    {/* Old automations route disabled - keeping for fallback if needed */}
                                    {/* <Route path="/automations" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <Automations />
                                      </PermissionGuard>
                                    } /> */}
                                    <Route path="/automations" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <AutomationsV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/automations-v2" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <AutomationsV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/gps" element={<GPS />} />
                                    <Route path="/online-bookings" element={<Navigate to="/settings" state={{ from: '/online-bookings' }} replace />} />
                                    <Route path="/online-booking-editor" element={<OnlineBookingEditor />} />

                                    <Route path="/messages" element={
                                      <PermissionGuard permission="messages.pageAccess">
                                        <Messages />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/calendarV2" element={
                                      <PermissionGuard permission="calendar.pageAccess">
                                        <CalendarV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/mobile-calendar" element={
                                      <PermissionGuard permission="calendar.pageAccess">
                                        <MobileCalendar />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/supplies-usage" element={<SuppliesUsage />} />
                                    <Route path="/crm" element={
                                      <PermissionGuard permission="crm.pageAccess">
                                        <CRM />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/profile" element={<Profile />} />
                                    <Route path="/test-notification" element={<TestNotification />} />
                                  <Route path="/rex-testing" element={<RexTesting />} />
                                  <Route path="/rex-training" element={<RexTraining />} />
                                  <Route path="/website" element={<Website />} />
                                    <Route path="/dialler" element={<Dialler />} />
                                    <Route path="/calls-beta" element={<CallsBeta />} />
                                    <Route path="/kpi-dashboard" element={
                                      <PermissionGuard permission="kpiDashboard.pageAccess">
                                        <KPIDashboard />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                                  </Route>
                                </Routes>
                              </ToastProvider>
                            </BrowserRouter>
                            </SimpleTimerProvider>
                          </CallProvider>
                          </TwilioMessagePollerProvider>
                        </UnreadOpportunitiesProvider>
                      </UnreadMessagesProvider>
                      </SubscriptionProvider>
                    </NotificationsProvider>
                </BookingStatus.Provider>
              </PermissionProvider>
            </ApplicationSettingsProvider>
          </DataCacheProvider>
        </BusinessProvider>
    </SupabaseProvider>
    {/* Removed SessionStatusIndicator */}
    <ToastContainer />
    </>
  );
};

export default App;
